/**
 * Minted ids for open vocabulary — the foundation of the opaque-identity
 * model (ADR-0004 §1, §6). Every user/pack-authored predicate, trait,
 * tuple field, enum value, calendar, dimension, and unit definition is
 * identified by an **opaque, immutable, kind-prefixed** id rather than a
 * tokenized name. Identity is decoupled from naming; the raw id is never
 * user-facing.
 *
 * Measurement vocabulary — `dimension` and `unit` — joined this model in
 * ADR-0007 §2: they were the lone open vocabulary that kept readable
 * dotted-token ids (`metric.km`, `dnd.gp`); they are now opaque
 * `dim_…` / `unit_…` definitions with a mandatory localized name + display
 * symbol, on par with predicates / traits / calendars.
 *
 * Ids are minted where the define-op is authored — client-side for
 * GM-authored defines (mirroring the `op_<uuid>` mint in
 * `packages/replica`), server-side for LLM-proposed defines (the LLM
 * never mints or types an id — ADR-0004 §7).
 *
 * The kind prefix guarantees a minted id can never collide with the
 * reserved sentinels (`__pack`, `__campaign`, `__entity`, `belief:`), and
 * the UUIDv4 body excludes the reserved fact-composition separators `#`
 * (instance) and `:` (belief) — making the `base#instance` /
 * `base#belief:<viewer>` composition strictly safe (ADR-0004 §1, §6).
 */

// `crypto.randomUUID` is available at runtime in modern browsers, Web
// Workers, Cloudflare Workers, and Node 19+ — we don't want to pull DOM
// lib types into every consumer just to type this one call. Mirrors the
// declarations in `identity.ts` / `entity.ts`.
declare const crypto: { randomUUID(): string };

/**
 * The kinds of open vocabulary that carry a minted, opaque id. This is the
 * extensible side of the §2 boundary — system-closed identifiers (entity
 * roots, the built-in `name` predicate, config keys) stay readable tokens.
 */
export type VocabIdKind =
  | 'predicate'
  | 'trait'
  | 'field'
  | 'enum'
  | 'calendar'
  | 'dimension'
  | 'unit';

/**
 * The kind→prefix map. Extending the vocabulary with a new kind is a
 * single-entry edit here. Prefixes are short, lowercase, and end in `_`;
 * none is a prefix of another, so `isVocabId` can match unambiguously.
 */
export const VOCAB_KIND_PREFIXES: Readonly<Record<VocabIdKind, string>> = {
  predicate: 'pred_',
  trait: 'trait_',
  field: 'fld_',
  enum: 'enm_',
  calendar: 'cal_',
  dimension: 'dim_',
  unit: 'unit_',
};

/**
 * The minted-id body charset. A UUIDv4 is hex digits + hyphens, which is
 * URL-safe (RFC 3986 unreserved) and — crucially — contains neither `#`
 * nor `:`, the reserved fact-composition separators (ADR-0004 §6).
 */
const VOCAB_ID_BODY = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Mint a new opaque vocabulary id for `kind`. Format: `<prefix><uuidv4>`,
 * e.g. `pred_2f1c…`. Collision-resistant (UUIDv4) and URL-safe; the body
 * excludes `#` and `:` by construction.
 */
export function mintVocabId(kind: VocabIdKind): string {
  return `${VOCAB_KIND_PREFIXES[kind]}${crypto.randomUUID()}`;
}

/**
 * Validate that `s` is a well-formed minted id of the given `kind`:
 * correct prefix + a UUIDv4 body. Reserved sentinels (`__pack`,
 * `__campaign`, `__entity`, `belief:`) and anything containing `#` / `:`
 * are unproducible by `mintVocabId` and rejected here.
 */
export function isVocabId(kind: VocabIdKind, s: string): boolean {
  const prefix = VOCAB_KIND_PREFIXES[kind];
  if (!s.startsWith(prefix)) return false;
  const body = s.slice(prefix.length);
  return VOCAB_ID_BODY.test(body);
}
